import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api } from '../../services/api'
import type {
  CortexCredentialEntry,
  CortexMcpServer,
  CortexMeSettings,
  CortexModelInfo,
  CortexUserSidecarSummary,
} from '../../services/api'
import CredentialEditModal, { type CredentialEntryView } from './CredentialEditModal'
import './AriaSettings.css'

/// Scope of the credentials being edited: tenant-level (organization-wide,
/// Aria's own identity) vs. user-level (personal delegate — Aria acts on
/// behalf of THIS logged-in user with their personal credentials).
type EditScope = { scope: 'tenant'; service: string } | { scope: 'user'; service: string }

const SIDECAR_META: Record<string, { label: string; description: string }> = {
  websearch:   { label: 'Websuche',  description: 'Web-Recherche via SearXNG' },
  erpnext:     { label: 'ERPNext',   description: 'Buchhaltung, Rechnungen, CRM' },
  forgejo:     { label: 'Forgejo',   description: 'Git-Repositories, Issues, PRs' },
  bitwarden:   { label: 'Bitwarden', description: 'Passwort-Verwaltung' },
  mail:        { label: 'Mail',      description: 'E-Mail-Versand und -Verwaltung' },
  opnsense:    { label: 'OPNsense',  description: 'Firewall-Verwaltung' },
  portal:      { label: 'Portal',    description: 'Portal-Verwaltung' },
  lexoffice:   { label: 'lexoffice', description: 'Buchhaltung lexoffice' },
  kubectl:     { label: 'kubectl',   description: 'Kubernetes-Verwaltung' },
  proxmox:     { label: 'Proxmox',   description: 'Virtualisierungs-Verwaltung' },
  ssh:         { label: 'SSH',       description: 'Server-Zugriff' },
  git:         { label: 'Git',       description: 'Git-Operationen' },
  caldav:      { label: 'Kalender',  description: 'CalDAV Kalender-Verwaltung' },
  'sidecar-mgr': { label: 'Sidecar Manager', description: 'MCP-Sidecar-Verwaltung' },
}

function describeSidecar(name: string): { label: string; description: string } {
  return SIDECAR_META[name] ?? {
    label: name.charAt(0).toUpperCase() + name.slice(1),
    description: `MCP-Sidecar ${name}`,
  }
}

function sidecarBadge(s: CortexMcpServer): { className: string; label: string; title: string } {
  // Two signals: pod-side "connected" (MCP-Server up + advertises tools) and
  // per-tenant "credentials_status" (can Aria actually use it for this org?).
  // Show the most user-meaningful state.
  if (!s.connected || s.tool_count === 0) {
    return {
      className: 'aria-sidecar-badge--err',
      label: 'Server offline',
      title: 'MCP-Sidecar antwortet nicht oder hat keine Tools — IT-SCE Support kontaktieren.',
    }
  }
  switch (s.credentials_status) {
    case 'ready':
      return {
        className: 'aria-sidecar-badge--ok',
        label: 'Einsatzbereit',
        title: 'Sidecar online und Credentials für diese Organisation gesetzt — Aria kann es nutzen.',
      }
    case 'missing':
      return {
        className: 'aria-sidecar-badge--warn',
        label: 'Credentials fehlen',
        title: 'Sidecar online, aber Ihre Organisation hat noch keine Anmeldedaten hinterlegt. In den Service-Settings einrichten.',
      }
    case 'not_granted':
      return {
        className: 'aria-sidecar-badge--muted',
        label: 'Nicht freigeschaltet',
        title: 'Dieser Sidecar ist für Ihre Organisation nicht im Paket. IT-SCE Vertrieb kontaktieren falls gewünscht.',
      }
    case 'shared':
      return {
        className: 'aria-sidecar-badge--ok',
        label: 'Verbunden',
        title: 'Geteilte Infrastruktur — keine organisationsspezifischen Anmeldedaten nötig.',
      }
    default:
      // Backend ohne credentials_status (alte Version): fallback auf alten Text
      return {
        className: 'aria-sidecar-badge--ok',
        label: 'Verbunden',
        title: 'Sidecar online.',
      }
  }
}

interface AriaSettingsProps {
  // Cockpit-Org-Slug — wird an Cortex `/api/v1/mcp/status?tenant=` weitergegeben
  // damit IT-SCE-Mitarbeiter die Sidecars der gerade angezeigten Org sehen,
  // nicht ihre eigenen (sonst zeigt das EZA-Cockpit Patric's IT-SCE-Sicht).
  tenant?: string
}

export default function AriaSettings({ tenant }: AriaSettingsProps = {}) {
  const { token } = useAuth()

  const [models, setModels] = useState<CortexModelInfo[]>([])
  const [modelsLoading, setModelsLoading] = useState(false)
  const [modelsUnavailable, setModelsUnavailable] = useState(false)
  const [meSettings, setMeSettings] = useState<CortexMeSettings | null>(null)
  const [savingModel, setSavingModel] = useState(false)
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null)
  const [settingsError, setSettingsError] = useState<string | null>(null)

  const [sidecars, setSidecars] = useState<CortexMcpServer[]>([])
  const [sidecarsLoading, setSidecarsLoading] = useState(false)
  const [sidecarsError, setSidecarsError] = useState<string | null>(null)

  // Map of service_type → credential entries (masked). Used to seed the edit
  // modal so the admin sees which credential_kinds already exist + their
  // last-4 preview before typing new values.
  const [credentialsByService, setCredentialsByService] = useState<
    Record<string, CortexCredentialEntry[]>
  >({})

  // User-level (personal-delegate) credentials: which sidecars has the
  // logged-in user already granted Aria personal access to?
  const [userCredentials, setUserCredentials] = useState<CortexUserSidecarSummary[]>([])

  const [editing, setEditing] = useState<EditScope | null>(null)

  /// Result of the most-recent "Verbindung testen" click per service. Each
  /// entry stores `pending`, `ok` or `error` plus a message. Cleared when the
  /// user reloads sidecars or saves new credentials.
  const [testResults, setTestResults] = useState<
    Record<string, { status: 'pending' | 'ok' | 'error'; message: string; durationMs?: number }>
  >({})

  const runTest = useCallback(
    async (service: string) => {
      if (!tenant) return
      setTestResults(prev => ({
        ...prev,
        [service]: { status: 'pending', message: 'Teste Verbindung…' },
      }))
      try {
        const r = await api.testTenantCredentials(tenant, service)
        setTestResults(prev => ({
          ...prev,
          [service]: {
            status: r.ok ? 'ok' : 'error',
            message: r.message,
            durationMs: r.duration_ms,
          },
        }))
      } catch (err) {
        const message =
          err && typeof err === 'object' && 'error' in err
            ? String((err as { error: string }).error)
            : 'Test fehlgeschlagen.'
        setTestResults(prev => ({
          ...prev,
          [service]: { status: 'error', message },
        }))
      }
    },
    [tenant],
  )

  const load = useCallback(async () => {
    if (!token) return
    api.setToken(token)
    setSettingsError(null)
    setSettingsMessage(null)

    setModelsLoading(true)
    setModelsUnavailable(false)
    try {
      const list = await api.listCortexModels()
      setModels(list)
    } catch (err) {
      console.error('[AriaSettings] Failed to list models:', err)
      // Cortex nicht erreichbar: kein roter Fehlerkasten, nur ein neutraler Hinweis (Modellliste ist optional).
      setModelsUnavailable(true)
    } finally {
      setModelsLoading(false)
    }

    try {
      const me = await api.getCortexMeSettings()
      setMeSettings(me)
    } catch (err) {
      console.error('[AriaSettings] Failed to load me settings:', err)
    }

    // TQ #5452: Sidecar-Liste nur laden wenn der Cockpit-Tenant aufgelöst ist.
    // Ohne `tenant` würde der API-Call ohne `?tenant=` ausgehen — IT-SCE-Staff
    // sähen dann ihren eigenen `itsce`-Tenant statt der besuchten Kunden-Org
    // (21 Sidecars statt der erwarteten Plan-Teilmenge).
    if (!tenant) {
      setSidecars([])
      setSidecarsLoading(false)
      setSidecarsError(null)
    } else {
      setSidecarsLoading(true)
      setSidecarsError(null)
      try {
        const status = await api.getCortexMcpStatus(tenant)
        const sorted = [...status.servers].sort((a, b) => a.name.localeCompare(b.name))
        setSidecars(sorted)
      } catch (err) {
        console.error('[AriaSettings] Failed to load MCP status:', err)
        setSidecarsError('Sidecar-Status konnte nicht geladen werden.')
      } finally {
        setSidecarsLoading(false)
      }
    }

    // Load existing tenant credentials for the edit modal. Org-Owners
    // (mcp-workspace) can read their own tenant; IT-SCE staff can read any.
    // Failure is non-fatal — the edit modal just falls back to empty fields.
    if (tenant) {
      try {
        const creds = await api.listTenantCredentials(tenant)
        setCredentialsByService(creds.services)
      } catch (err) {
        console.warn('[AriaSettings] Failed to load credentials for edit form:', err)
        setCredentialsByService({})
      }
    }

    // Personal delegate credentials (current user). Cortex returns whichever
    // sidecars the user has set, with the credential keys (no secrets).
    try {
      const list = await api.listUserCredentials()
      setUserCredentials(list)
    } catch (err) {
      console.warn('[AriaSettings] Failed to load user credentials:', err)
      setUserCredentials([])
    }
  }, [token, tenant])

  useEffect(() => {
    void load()
  }, [load])

  const handleModelChange = useCallback(async (modelId: string) => {
    if (!token) return
    api.setToken(token)
    setSavingModel(true)
    setSettingsError(null)
    setSettingsMessage(null)
    try {
      const updated = await api.updateCortexMeSettings({ preferred_model: modelId || null })
      setMeSettings(updated)
      setSettingsMessage('Modell-Auswahl gespeichert.')
    } catch (err) {
      console.error('[AriaSettings] Failed to update model:', err)
      setSettingsError('Speichern fehlgeschlagen.')
    } finally {
      setSavingModel(false)
    }
  }, [token])

  return (
    <div className="aria-settings-block">
      <section className="aria-settings-card">
        <h2>Aria — Modell-Auswahl</h2>
        <p className="aria-settings-subtitle">
          Bestimmt, welches Modell Aria standardmäßig nutzt.
          Aktuelle Wahl: <strong>{meSettings?.preferred_model ?? '—'}</strong>
          {meSettings?.overrides?.preferred_model === null && (
            <span className="aria-settings-note"> (Tenant-Default)</span>
          )}
        </p>
        {modelsLoading ? (
          <p className="aria-settings-note">Modelle werden geladen…</p>
        ) : (
          <div className="aria-settings-row">
            <select
              className="aria-settings-select"
              value={meSettings?.overrides?.preferred_model ?? ''}
              disabled={savingModel || !token}
              onChange={(e) => handleModelChange(e.target.value)}
            >
              <option value="">— Tenant-Default verwenden —</option>
              {models.map(m => (
                <option key={`${m.provider}:${m.id}`} value={m.id}>
                  {m.name || m.id} ({m.provider})
                </option>
              ))}
            </select>
            {savingModel && <span className="aria-settings-note">Wird gespeichert…</span>}
          </div>
        )}
        {settingsMessage && <div className="aria-settings-success">{settingsMessage}</div>}
        {modelsUnavailable && !settingsError && (
          <div className="aria-settings-note" role="status">Die Modellliste ist gerade nicht abrufbar.</div>
        )}
        {settingsError && <div className="aria-settings-error">{settingsError}</div>}
      </section>

      <section className="aria-settings-card aria-settings-info">
        <h2>Wie Aria mit deinen Diensten arbeitet</h2>
        <p className="aria-settings-info-body">
          Aria hat <strong>ein eigenes Konto</strong> in eurer Organisation
          (z.B. <code>aria@…</code> als Mailbox, ein eigener Nextcloud-User).
          Damit kann sie in <em>ihrem Namen</em> Mails versenden, Daten in der
          Org-Cloud ablegen oder ERPNext-Belege anlegen.
        </p>
        <p className="aria-settings-info-body">
          Wenn du möchtest dass Aria <strong>in deinem persönlichen Auftrag</strong>{' '}
          handelt — also deine private Mailbox liest oder deinen Kalender bearbeitet
          — gib ihr darunter eine <strong>persönliche Vollmacht</strong>. Diese
          Credentials gelten nur für dich und überschreiben für deine Anfragen
          die Org-Credentials.
        </p>
      </section>

      <section className="aria-settings-card">
        <h2>Aria — Verbundene Sidecars <span className="aria-settings-tag">Org-Level</span></h2>
        <p className="aria-settings-subtitle">
          Status der MCP-Sidecars, die für deine Organisation freigegeben sind.
          Aria nutzt diese Credentials in <em>ihrem Namen</em>.
        </p>
        {sidecarsLoading ? (
          <p className="aria-settings-note">Sidecar-Status wird geladen…</p>
        ) : sidecarsError ? (
          <div className="aria-settings-error">{sidecarsError}</div>
        ) : sidecars.length === 0 ? (
          <p className="aria-settings-note">Keine Sidecars verfügbar.</p>
        ) : (
          <ul className="aria-sidecar-list">
            {sidecars.map(s => {
              const meta = describeSidecar(s.name)
              const badge = sidecarBadge(s)
              // "shared" services (websearch, sidecar-mgr) don't have per-tenant
              // credentials — hide the edit button for them.
              const editable =
                s.credentials_status !== 'shared' &&
                s.credentials_status !== undefined
              return (
                <li key={s.name} className="aria-sidecar-item">
                  <div className="aria-sidecar-icon" aria-hidden="true">
                    {meta.label.charAt(0)}
                  </div>
                  <div className="aria-sidecar-meta">
                    <div className="aria-sidecar-name">{meta.label}</div>
                    <div className="aria-sidecar-desc">{meta.description}</div>
                  </div>
                  <div className="aria-sidecar-status">
                    <span className={`aria-sidecar-badge ${badge.className}`} title={badge.title}>
                      {badge.label}
                    </span>
                    <span className="aria-sidecar-tools">{s.tool_count} Tools</span>
                  </div>
                  {editable && tenant && (
                    <>
                      <button
                        className="aria-sidecar-test"
                        onClick={() => void runTest(s.name)}
                        disabled={
                          testResults[s.name]?.status === 'pending' ||
                          s.credentials_status === 'missing' ||
                          s.credentials_status === 'not_granted'
                        }
                        title={(() => {
                          const tr = testResults[s.name]
                          if (!tr) return 'Live-Test gegen den Ziel-Service'
                          if (tr.status === 'pending') return tr.message
                          const dur = tr.durationMs ? ` (${tr.durationMs}ms)` : ''
                          return `${tr.message}${dur}`
                        })()}
                      >
                        {(() => {
                          const tr = testResults[s.name]
                          if (!tr) return 'Testen'
                          if (tr.status === 'pending') return '…'
                          if (tr.status === 'ok') return '✓ OK'
                          return '✗ Fehler'
                        })()}
                      </button>
                      <button
                        className="aria-sidecar-edit"
                        onClick={() => setEditing({ scope: 'tenant', service: s.name })}
                        title="Org-Credentials bearbeiten (Aria handelt in IHREM Namen)"
                      >
                        Bearbeiten
                      </button>
                    </>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="aria-settings-card">
        <h2>
          Meine persönliche Vollmacht <span className="aria-settings-tag aria-settings-tag--user">Nur du</span>
        </h2>
        <p className="aria-settings-subtitle">
          Hier kannst du Aria <strong>für dein eigenes Konto</strong> bevollmächtigen
          (z.B. deine private Mailbox, dein persönlicher Kalender). Andere
          Mitarbeiter sehen diese Credentials nicht.
        </p>

        {userCredentials.length === 0 ? (
          <p className="aria-settings-note">
            Du hast Aria noch keine persönliche Vollmacht erteilt. Klick unten auf
            einen Service, um zu beginnen.
          </p>
        ) : (
          <ul className="aria-sidecar-list">
            {userCredentials.map(uc => {
              const meta = describeSidecar(uc.sidecar_name)
              return (
                <li key={uc.sidecar_name} className="aria-sidecar-item">
                  <div className="aria-sidecar-icon" aria-hidden="true">{meta.label.charAt(0)}</div>
                  <div className="aria-sidecar-meta">
                    <div className="aria-sidecar-name">{meta.label}</div>
                    <div className="aria-sidecar-desc">
                      {uc.keys.length} Feld(er) hinterlegt
                    </div>
                  </div>
                  <button
                    className="aria-sidecar-edit"
                    onClick={() => setEditing({ scope: 'user', service: uc.sidecar_name })}
                    title="Persönliche Aria-Vollmacht bearbeiten"
                  >
                    Bearbeiten
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {/* Quick-Add: services where the user could grant a personal delegate
            but hasn't yet. Reuses the Org-Level sidecar list as the catalogue
            (a service that's not licensed for the tenant won't show here). */}
        <div className="aria-user-add-row">
          {sidecars
            .filter(s =>
              s.credentials_status !== 'shared' &&
              !userCredentials.some(uc => uc.sidecar_name === s.name)
            )
            .map(s => (
              <button
                key={s.name}
                className="aria-user-add-btn"
                onClick={() => setEditing({ scope: 'user', service: s.name })}
                title={`Aria persönliche Vollmacht für ${describeSidecar(s.name).label} geben`}
              >
                + {describeSidecar(s.name).label}
              </button>
            ))}
        </div>
      </section>

      {editing && (
        editing.scope === 'tenant' && tenant ? (
          <CredentialEditModal
            service={editing.service}
            existing={(credentialsByService[editing.service] ?? []).map(e => ({
              kind: e.credential_kind,
              preview: e.secret_preview,
            }))}
            title="Org-Credentials bearbeiten"
            subtitle={`${describeSidecar(editing.service).label} — gilt für die ganze Organisation`}
            hint="Diese Credentials nutzt Aria in IHREM Namen für alle Mitarbeiter. Felder die leer bleiben werden nicht verändert."
            onSave={async (values) => {
              const payload: Record<string, { secret: string }> = {}
              for (const [k, v] of Object.entries(values)) payload[k] = { secret: v }
              const r = await api.setTenantCredentials(tenant, editing.service, payload)
              return { kindsUpdated: r.kinds_updated }
            }}
            onDelete={async () => {
              await api.deleteTenantCredentials(tenant, editing.service)
            }}
            onClose={() => setEditing(null)}
            onSaved={() => void load()}
          />
        ) : editing.scope === 'user' ? (
          <UserCredentialEditModal
            service={editing.service}
            onClose={() => setEditing(null)}
            onSaved={() => void load()}
          />
        ) : null
      )}
    </div>
  )
}

/// Wrapper that fetches the masked user-credential entries before opening the
/// generic modal. Kept inline because it's tightly coupled to AriaSettings.
function UserCredentialEditModal({
  service,
  onClose,
  onSaved,
}: {
  service: string
  onClose: () => void
  onSaved: () => void
}) {
  const [existing, setExisting] = useState<CredentialEntryView[] | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .getUserCredentials(service)
      .then(info => {
        if (cancelled) return
        setExisting(info.keys.map(k => ({ kind: k.key, preview: k.value_preview })))
      })
      .catch(() => {
        if (!cancelled) setExisting([])
      })
    return () => {
      cancelled = true
    }
  }, [service])

  // Render a placeholder while we fetch — the user sees the modal frame instantly
  // but the fields populate once we know what kinds already exist.
  if (existing === null) {
    return (
      <div className="cred-modal-backdrop" onClick={onClose}>
        <div className="cred-modal" onClick={(e) => e.stopPropagation()}>
          <p className="aria-settings-note">Lade Credentials…</p>
        </div>
      </div>
    )
  }

  const meta = describeSidecar(service)
  return (
    <CredentialEditModal
      service={service}
      existing={existing}
      title="Persönliche Aria-Vollmacht"
      subtitle={`${meta.label} — gilt nur für dich`}
      hint="Mit diesen Credentials handelt Aria in DEINEM Namen — wenn du sie z.B. nach 'Mails' fragst, nutzt sie diese statt ihrer Org-Credentials. Andere sehen sie nicht."
      onSave={async (values) => {
        const r = await api.setUserCredentials(service, values)
        return { kindsUpdated: Object.keys(values).slice(0, r.keys_set) }
      }}
      onDelete={async () => {
        await api.deleteUserCredentials(service)
      }}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}
